import { effect, signal } from '@preact/signals';
import { io, type Socket } from 'socket.io-client';
import * as alerts from './alerts';

export type Role = 'agent' | 'manager' | 'admin';
export type Me = { id: number; name: string; email: string; role: Role; offices: string[] };
export type Conversation = {
  id: string; name: string; email: string | null; phone: string | null; zip: string; office: string | null;
  state: 'ringing' | 'active' | 'ended' | 'missed' | 'offline'; agentId: number | null; agentName: string | null;
  pageUrl: string | null; createdAt: string; acceptedAt: string | null; preview: string | null;
};
export type Message = { id: number; conversationId: string; sender: 'visitor' | 'agent' | 'system' | 'bot'; agentName: string | null; body: string; createdAt: string };
export type Ring = { conversation: Conversation; stage: 'office' | 'all'; expiresAt: number; durationMs: number };
export type Presence = { id: number; name: string; offices: string[]; available: boolean };
export type Chat = { conversation: Conversation; messages: Message[]; unread: number; visitorTyping?: boolean; waiting?: boolean; visitorAway?: boolean };
export type SavedReply = { id: number; title: string; body: string; sortOrder: number };

export const me = signal<Me | null>(null);
export const connection = signal<'connecting' | 'online' | 'offline'>('connecting');
export const available = signal(false);
export const presence = signal<Presence[]>([]);
export const rings = signal<Ring[]>([]);
export const chats = signal<Record<string, Chat>>({});
export const selected = signal<string | null>(null);
export const toast = signal<string | null>(null);
/** Set when this agent let a chat ring out without responding — asks "still available?". */
export const stillThere = signal(false);
export const savedReplies = signal<SavedReply[]>([]);
export const offices = signal<string[]>([]);

let socket: Socket | null = null;
let toastTimer: number | undefined;

export function flash(text: string) {
  toast.value = text;
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => (toast.value = null), 5000);
}

const label = (c: Conversation) => `${c.office?.replace(/, [A-Z]{2}$/, '') ?? 'Any office'} — ${c.name}`;

// Keep the alert system in step with what's ringing.
effect(() => {
  const list = rings.value.map((r) => ({ id: r.conversation.id, label: label(r.conversation) }));
  if (list.length) alerts.ring(list, (id) => (selected.value = `ring:${id}`));
  else alerts.stop();
});

function upsertChat(conversation: Conversation, messages?: Message[]) {
  const current = chats.value[conversation.id];
  chats.value = { ...chats.value, [conversation.id]: { ...current, conversation, messages: messages ?? current?.messages ?? [], unread: current?.unread ?? 0 } };
}

function patchChat(id: string, patch: Partial<Chat>) {
  const chat = chats.value[id];
  if (chat) chats.value = { ...chats.value, [id]: { ...chat, ...patch } };
}

const typingTimers = new Map<string, number>();

export function connect() {
  if (socket) return socket;
  socket = io('/agent', { withCredentials: true, transports: ['websocket', 'polling'] });

  socket.on('connect', () => (connection.value = 'online'));
  socket.on('disconnect', () => (connection.value = 'offline'));
  socket.on('connect_error', (err) => {
    connection.value = 'offline';
    if (err.message === 'unauthorized') location.reload();
  });

  socket.on('snapshot', (s: { available: boolean; chats: { conversation: Conversation; messages: Message[] }[]; ringing: Ring[]; presence: Presence[] }) => {
    available.value = s.available;
    presence.value = s.presence;
    rings.value = s.ringing;
    for (const c of s.chats) upsertChat(c.conversation, c.messages);
    if (!selected.value && s.chats[0]) selected.value = s.chats[0].conversation.id;
  });

  socket.on('presence', (p: Presence[]) => {
    presence.value = p;
    const mine = p.find((x) => x.id === me.value?.id);
    if (mine) available.value = mine.available;
  });

  socket.on('ring', (r: Ring) => {
    rings.value = [...rings.value.filter((x) => x.conversation.id !== r.conversation.id), r];
  });

  socket.on('ring:stop', (p: { id: string; reason: string; by?: string }) => {
    const was = rings.value.find((r) => r.conversation.id === p.id);
    rings.value = rings.value.filter((r) => r.conversation.id !== p.id);
    if (!was) return;
    if (p.reason === 'taken') flash(`${was.conversation.name}'s chat was taken by ${p.by}.`);
    if (p.reason === 'missed') {
      flash(`Nobody answered ${was.conversation.name} — they were shown the call/email message and saved as a lead.`);
      stillThere.value = true;
    }
    if (p.reason === 'left') flash(`${was.conversation.name} left before anyone answered.`);
  });

  socket.on('message', (m: Message) => {
    const chat = chats.value[m.conversationId];
    if (!chat || chat.messages.some((x) => x.id === m.id)) return;
    const unread = selected.value === m.conversationId && document.hasFocus() ? 0 : chat.unread + (m.sender === 'visitor' ? 1 : 0);
    chats.value = { ...chats.value, [m.conversationId]: { ...chat, messages: [...chat.messages, m], unread } };
    if (m.sender === 'visitor') {
      alerts.blip();
      patchChat(m.conversationId, { visitorTyping: false });
    }
    if (m.sender === 'agent') {
      patchChat(m.conversationId, { waiting: false });
      alerts.clearNudge(m.conversationId);
    }
  });

  socket.on('typing', (t: { id: string; typing: boolean }) => {
    patchChat(t.id, { visitorTyping: t.typing });
    clearTimeout(typingTimers.get(t.id));
    if (t.typing) typingTimers.set(t.id, window.setTimeout(() => patchChat(t.id, { visitorTyping: false }), 6000));
  });

  socket.on('nudge', (n: { id: string; waitingSeconds: number }) => {
    const chat = chats.value[n.id];
    if (!chat) return;
    patchChat(n.id, { waiting: true });
    alerts.nudge(n.id, chat.conversation.name, n.waitingSeconds, (id) => (selected.value = id));
  });

  socket.on('nudge:clear', (n: { id: string }) => {
    patchChat(n.id, { waiting: false });
    alerts.clearNudge(n.id);
  });

  socket.on('visitor', (v: { id: string; present: boolean }) => patchChat(v.id, { visitorAway: !v.present }));

  socket.on('status', (s: { id?: string; state: string }) => {
    const chat = s.id ? chats.value[s.id] : undefined;
    if (chat && s.state === 'ended') upsertChat({ ...chat.conversation, state: 'ended' });
  });

  return socket;
}

function call<T>(event: string, payload?: unknown): Promise<T> {
  return new Promise((resolve) => connect().timeout(10_000).emit(event, payload, (err: unknown, res: T) => resolve(err ? ({ ok: false, reason: 'Connection problem — try again.' } as T) : res)));
}

export async function setAvailable(on: boolean) {
  const r = await call<{ ok: boolean; available: boolean }>('presence:set', on);
  if (r.ok) available.value = r.available;
  stillThere.value = false;
  return r.ok;
}

export async function accept(id: string) {
  const r = await call<{ ok: boolean; reason?: string; conversation?: Conversation; messages?: Message[] }>('chat:accept', id);
  if (!r.ok || !r.conversation) {
    flash(r.reason ?? 'Could not accept this chat.');
    rings.value = rings.value.filter((x) => x.conversation.id !== id);
    return false;
  }
  rings.value = rings.value.filter((x) => x.conversation.id !== id);
  upsertChat(r.conversation, r.messages);
  selected.value = id;
  return true;
}

export async function send(id: string, body: string) {
  const r = await call<{ ok: boolean }>('chat:message', { id, body });
  if (!r.ok) flash('Message not sent — the chat may have ended.');
}

let lastTyping = 0;
let typingOff: number | undefined;
/** Tell the visitor we're typing — at most every 2 s, and "stopped" after 3 s idle. */
export function typing(id: string, active: boolean) {
  clearTimeout(typingOff);
  if (active) {
    if (Date.now() - lastTyping > 2000) {
      connect().emit('chat:typing', { id, typing: true });
      lastTyping = Date.now();
    }
    typingOff = window.setTimeout(() => typing(id, false), 3000);
  } else if (lastTyping) {
    connect().emit('chat:typing', { id, typing: false });
    lastTyping = 0;
  }
}

export async function transfer(id: string, office: string | null) {
  const r = await call<{ ok: boolean; reason?: string }>('chat:transfer', { id, office });
  if (!r.ok) return flash(r.reason ?? 'Could not transfer this chat.');
  alerts.clearNudge(id);
  dismissChat(id);
  flash(`Transferred — ringing ${office ?? 'everyone available'}.`);
}

export async function loadReference() {
  const [replies, officeList] = await Promise.all([api<SavedReply[]>('/saved-replies'), api<string[]>('/offices')]);
  savedReplies.value = replies;
  offices.value = officeList;
}

export async function end(id: string) {
  const r = await call<{ ok: boolean }>('chat:end', id);
  const chat = chats.value[id];
  if (r.ok && chat) upsertChat({ ...chat.conversation, state: 'ended' });
}

export function markRead(id: string) {
  const chat = chats.value[id];
  if (chat?.unread) chats.value = { ...chats.value, [id]: { ...chat, unread: 0 } };
}

export function dismissChat(id: string) {
  const { [id]: _, ...rest } = chats.value;
  chats.value = rest;
  if (selected.value === id) selected.value = Object.keys(rest)[0] ?? null;
}

/* ---------------------------------------------------------------- HTTP -- */

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
    ...init,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error((data as { error?: string }).error ?? `HTTP ${res.status}`), { status: res.status });
  return data as T;
}
